import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  CategoryRevenue,
  CategoryProduct,
  DashboardSummary,
  DashboardToday,
  EmployeeRevenue,
  SalesHistoryRow,
} from "../api";
import { formatVND, formatNumber, formatTime, paymentLabel } from "../utils";
import { exportDashboardCsv } from "../utils/exportDashboardCsv";
import { normalizeSearchText, searchItems } from "../search";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { GlassPanel, KpiCard, Modal, toast } from "../components";
import { RevenueComparisonChart } from "../components/RevenueComparisonChart";
import { DataTable, Column } from "../components/DataTable";
import { useCountUp } from "../hooks";

interface DayPoint {
  day: string;
  sales: number;
  orders: number;
}

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return "vừa xong";
  if (seconds < 60) return `${seconds} giây trước`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} phút trước`;
}

type TimeTab = "today" | "week" | "month" | "year" | "custom";

// Format date theo local VN (UTC+7) — tránh toISOString lệch 7h.
function fmtLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TAB_CONFIG: { key: TimeTab; label: string }[] = [
  { key: "today", label: "Hôm nay" },
  { key: "week", label: "Tuần này" },
  { key: "month", label: "Tháng này" },
  { key: "year", label: "Năm này" },
  { key: "custom", label: "Tuỳ chỉnh" },
];

function getDateRange(tab: TimeTab, customFrom?: string, customTo?: string): { from: string; to: string } {
  const today = new Date();
  // fmt theo local VN (UTC+7) — toISOString lệch 7h, dùng local date parts thay vì UTC.
  const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  // Calendar boundaries: tuần Thứ 2→CN, tháng 1→cuối, năm 1/1→31/12
  const startOfWeek = (d: Date) => { // Thứ 2 = ngày đầu tuần (VN)
    const r = new Date(d);
    r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
    r.setHours(0, 0, 0, 0);
    return r;
  };
  const endOfWeek = (d: Date) => { // Chủ nhật = đầu tuần + 6
    const r = startOfWeek(d);
    r.setDate(r.getDate() + 6);
    return r;
  };
  const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const startOfYear = (d: Date) => new Date(d.getFullYear(), 0, 1);
  const endOfYear = (d: Date) => new Date(d.getFullYear(), 11, 31);
  switch (tab) {
    case "custom":
      return { from: customFrom || fmt(startOfMonth(today)), to: customTo || fmt(today) };
    case "today":
      return { from: fmt(today), to: fmt(today) };
    case "week":
      return { from: fmt(startOfWeek(today)), to: fmt(endOfWeek(today)) };
    case "month":
      return { from: fmt(startOfMonth(today)), to: fmt(endOfMonth(today)) };
    case "year":
      return { from: fmt(startOfYear(today)), to: fmt(endOfYear(today)) };
  }
}
type SoldAgg = { item_name: string; sku: string; qty: number; revenue: number };

/** Khóa sắp xếp panel "SP nhóm" — chỉ 2 trường có sẵn trong payload, sort ở client. */
type CatSortKey = "revenue" | "qty";

export default function DashboardPage() {
  const [data, setData] = useState<DashboardToday | null>(null);
  const [byCat, setByCat] = useState<CategoryRevenue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TimeTab>("today");
  const [periodLabel, setPeriodLabel] = useState("Hôm nay");
  // Custom range state cho tab "Tuỳ chỉnh"
  const todayStr = fmtLocal(new Date());
  const monthStartStr = fmtLocal(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [customFrom, setCustomFrom] = useState(monthStartStr);
  const [customTo, setCustomTo] = useState(todayStr);
  // Day-by-day breakdown cho custom range (area chart + table)
  const [dayPoints, setDayPoints] = useState<DayPoint[]>([]);
  const dateRange = useMemo(() => getDateRange(tab, customFrom, customTo), [tab, customFrom, customTo]);
  const [productQ, setProductQ] = useState("");
  const [searchHits, setSearchHits] = useState<SoldAgg[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRequestRef = useRef(0);

  // In-place search SP đã bán trong panel (debounce 300ms) — không nhảy /sales-history
  useEffect(() => {
    const requestId = ++searchRequestRef.current;
    const q = productQ.trim();
    if (!q) {
      setSearchHits(null);
      setSearchLoading(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          const rows: SalesHistoryRow[] = [];
          const limit = 100;
          let offset = 0;
          let total = 0;
          let pageSize = 0;
          do {
            const page = await api.salesHistory({ from: dateRange.from, to: dateRange.to, q, limit, offset });
            rows.push(...page.rows);
            total = page.total;
            pageSize = page.rows.length;
            offset += pageSize;
          } while (offset < total && pageSize > 0 && !cancelled && searchRequestRef.current === requestId);

          if (cancelled || searchRequestRef.current !== requestId) return;
          const map = new Map<string, SoldAgg>();
          for (const r of rows) {
            const key = r.item_name || "—";
            const cur = map.get(key);
            if (cur) {
              cur.qty += r.qty || 0;
              cur.revenue += r.revenue || 0;
            } else {
              map.set(key, { item_name: key, sku: r.sku || "—", qty: r.qty || 0, revenue: r.revenue || 0 });
            }
          }
          const list = searchItems(Array.from(map.values()), q, (item) => ({
            name: item.item_name,
            sku: item.sku,
          }))
            .sort((a, b) => b.item.revenue - a.item.revenue)
            .map(({ item }) => item);
          setSearchHits(list);
        } catch {
          if (!cancelled && searchRequestRef.current === requestId) setSearchHits([]);
        } finally {
          if (!cancelled && searchRequestRef.current === requestId) setSearchLoading(false);
        }
      })();
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [productQ, dateRange.from, dateRange.to]);

  // Drill-down: bấm nhóm hàng → modal SP đã bán của nhóm (kỳ đang xem)
  const [catDrill, setCatDrill] = useState<{ cat: CategoryRevenue | null; products: CategoryProduct[]; loading: boolean }>(
    { cat: null, products: [], loading: false }
  );
  const openCatDrill = useCallback((cat: CategoryRevenue) => {
    const range = getDateRange(tab, customFrom, customTo);
    setCatSort({ key: null, dir: "desc" }); // mở lại = thứ tự API nguyên bản (doanh thu giảm dần)
    setCatDrill({ cat, products: [], loading: true });
    api.categoryProducts(cat.category, range.from, range.to)
      .then((r) => setCatDrill({ cat, products: r.products, loading: false }))
      .catch((e) => { toast.error(e.message); setCatDrill({ cat, products: [], loading: false }); });
  }, [tab, customFrom, customTo]);
  const closeCatDrill = useCallback(() => setCatDrill({ cat: null, products: [], loading: false }), []);

  // Sắp xếp danh sách SP trong panel nhóm — client-side, mặc định key=null (giữ nguyên thứ tự API).
  const [catSort, setCatSort] = useState<{ key: CatSortKey | null; dir: "asc" | "desc" }>({ key: null, dir: "desc" });
  const pickCatSort = useCallback((key: CatSortKey) => {
    // Bấm lại cùng nút → đảo chiều; nút mới → mặc định giảm dần (lớn nhất trước).
    setCatSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  }, []);
  const catRows = useMemo(() => {
    const key = catSort.key;
    if (!key || catDrill.products.length < 2) return catDrill.products;
    const sign = catSort.dir === "asc" ? 1 : -1;
    // Giữ nguyên thứ tự gốc giữa các dòng bằng nhau (tie-break theo index ban đầu).
    return catDrill.products
      .map((p, i) => ({ p, i }))
      .sort((a, b) => {
        const av = Number(a.p[key]) || 0;
        const bv = Number(b.p[key]) || 0;
        return av === bv ? a.i - b.i : (av - bv) * sign;
      })
      .map(({ p }) => p);
  }, [catDrill.products, catSort]);

  // Drill-down SP: bấm SP → modal từng lần bán (ngày/giờ/sl/doanh thu)
  const [productDrill, setProductDrill] = useState<{
    name: string | null;
    rows: SalesHistoryRow[];
    loading: boolean;
  }>({ name: null, rows: [], loading: false });
  const productDrillRequestRef = useRef(0);
  const openProductDrill = useCallback((itemName: string) => {
    const requestId = ++productDrillRequestRef.current;
    const range = getDateRange(tab, customFrom, customTo);
    setProductDrill({ name: itemName, rows: [], loading: true });
    api.salesHistory({ from: range.from, to: range.to, q: itemName, limit: 100 })
      .then((res) => {
        const exactName = normalizeSearchText(itemName);
        const exactRows = res.rows.filter((r) => normalizeSearchText(r.item_name) === exactName);
        const rows = (exactRows.length > 0 ? exactRows : searchItems(
          res.rows,
          itemName,
          (row) => ({ name: row.item_name, sku: row.sku }),
        ).map(({ item }) => item)).sort((a, b) => {
            const ta = a.sold_at || a.imported_at || a.sale_date || "";
            const tb = b.sold_at || b.imported_at || b.sale_date || "";
            return tb.localeCompare(ta);
          });
        if (productDrillRequestRef.current === requestId) {
          setProductDrill({ name: itemName, rows, loading: false });
        }
      })
      .catch((e) => {
        if (productDrillRequestRef.current === requestId) {
          toast.error(e.message);
          setProductDrill({ name: itemName, rows: [], loading: false });
        }
      });
  }, [tab, customFrom, customTo]);
  const closeProductDrill = useCallback(() => {
    ++productDrillRequestRef.current;
    setProductDrill({ name: null, rows: [], loading: false });
  }, []);

  // Comparison data
  const [prevSales, setPrevSales] = useState<number | null>(null);

  // Auto-refresh state
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [, setTick] = useState(0); // force re-render for timeAgo

  const fetchDashboard = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    setError(null);

    const range = getDateRange(tab, customFrom, customTo);

    // Custom range: song song summary + range (day-by-day) để vẽ area chart + table.
    const summaryP = api.summary(range.from, range.to, tab === "today" || tab === "custom" ? undefined : tab);
    const rangeP = tab === "custom" ? api.range(range.from, range.to) : Promise.resolve(null);
    Promise.all([summaryP, rangeP])
      .then(([s, rangeRes]) => {
        setData({
          date: range.from,
          totalSales: s.totalSales,
          totalOrders: s.totalOrders,
          totalDiscount: s.totalDiscount,
          averageOrderValue: s.averageOrderValue,
          deltaSales: s.deltaSales,
          deltaOrders: s.deltaOrders,
          hourly: s.hourly,
          topItems: s.topItems,
          recentOrders: s.recentOrders,
        });
        setByCat(s.categories);
        setPrevSales(s.prevTotalSales);
        if (rangeRes) setDayPoints(rangeRes.days || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));

    const labels: Record<TimeTab, string> = {
      today: "Hôm nay",
      week: "Tuần này",
      month: "Tháng này",
      year: "Năm này",
      custom: `${range.from} → ${range.to}`,
    };
    setPeriodLabel(labels[tab]);
  }, [tab, customFrom, customTo]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Listen for global sync event
  useEffect(() => {
    const handler = () => fetchDashboard(true);
    window.addEventListener("app:resync", handler);
    return () => window.removeEventListener("app:resync", handler);
  }, [fetchDashboard]);

  // Auto-refresh every 30s — silent (no skeleton flash)
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) {
        fetchDashboard(true);
        setLastRefresh(new Date());
      }
    }, 30000);
    return () => clearInterval(id);
  }, [tab, fetchDashboard]);

  const sparklineSales = useMemo(
    () => (tab === "today" && data?.hourly ? data.hourly.map((h) => h.sales) : []),
    [tab, data?.hourly],
  );
  const maxCat = useMemo(
    () => byCat.reduce((m, c) => Math.max(m, c.revenue), 0),
    [byCat],
  );

  const comparisonText = useMemo(() => {
    if (prevSales === null || !data) return null;
    const current = data.totalSales;
    if (prevSales === 0) return null;
    const pct = ((current - prevSales) / prevSales) * 100;
    return { pct, prev: prevSales };
  }, [data, prevSales]);

  const catColor = (raw: string | null | undefined) => {
    if (!raw) return "#60a5fa";
    if (raw.startsWith("#")) return raw;
    const map: Record<string, string> = {
      RED: "#f87171", PINK: "#f472b6", ORANGE: "#fb923c", YELLOW: "#fbbf24",
      GREEN: "#34d399", BLUE: "#60a5fa", PURPLE: "#a78bfa", GREY: "#9ca3af", GRAY: "#9ca3af",
    };
    return map[raw.toUpperCase()] || "#60a5fa";
  };

  if (loading && !data) return <DashboardSkeleton />;
  if (error) return <div className="error">⚠️ {error}</div>;
  if (!data) return null;

  return (
    <>
      {/* Tab nhanh — không lọc Từ/Đến (trùng trang Doanh thu) */}
      <div className="tabs" style={{ marginBottom: 16, alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        {TAB_CONFIG.map((t) => (
          <button
            key={t.key}
            className={`tab-item ${tab === t.key ? "active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
        <span className="refresh-badge" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>schedule</span>
          {timeAgo(lastRefresh)}
        </span>
        {tab === "custom" && (
          <button
            className="btn btn-outline"
            style={{ marginLeft: "auto", fontSize: 12, padding: "6px 12px", minHeight: 32 }}
            onClick={async () => {
              try { const n = await exportDashboardCsv(dateRange.from, dateRange.to); toast.success(`Đã xuất ${n} dòng bán`); }
              catch (e) { toast.error((e as Error).message || "Xuất CSV lỗi"); }
            }}
            disabled={loading}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
            CSV
          </button>
        )}
      </div>

      {tab === "custom" && (
        <div className="filter-advanced" style={{ marginBottom: 12 }}>
          <div className="filter-field">
            <label>Từ ngày</label>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </div>
          <div className="filter-field">
            <label>Đến ngày</label>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
        </div>
      )}

      {/* KPI */}
      <div className="kpi-grid">
        <KpiCardPro
          label={`Doanh thu ${periodLabel.toLowerCase()}`}
          rawValue={data.totalSales}
          formatter={formatVND}
          delta={data.deltaSales}
          sparkline={sparklineSales}
          comparison={comparisonText}
        />
        <KpiCardPro
          label="Đơn hàng"
          rawValue={data.totalOrders}
          formatter={(v) => formatNumber(Math.round(v))}
          delta={data.deltaOrders}
          sub={`AOV ${formatVND(data.averageOrderValue)}`}
        />
        <KpiCard
          label="Giảm giá"
          value={formatVND(data.totalDiscount)}
          note={data.totalDiscount > 0 ? "đã áp dụng" : "không"}
          dotColor={data.totalDiscount > 0 ? "warn" : ""}
        />
        <KpiCard
          label="Giờ cao điểm"
          value={peakHourLabel(data.hourly)}
          note="theo doanh thu giờ"
          dotColor="blue"
        />
      </div>

      {tab === "today" ? (
        <HourlyChart hourly={data.hourly} />
      ) : tab === "custom" ? (
        <GlassPanel title="Biểu đồ doanh thu theo ngày" badge={`${dayPoints.length} ngày`} badgeVariant="info">
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dayPoints} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="#666" tickFormatter={(d: string) => d.slice(5)} fontSize={11} />
                <YAxis stroke="#666" fontSize={11} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10 }}
                  labelStyle={{ color: "#888" }}
                  formatter={(v: number) => formatVND(v)}
                />
                <Area type="monotone" dataKey="sales" stroke="#34d399" strokeWidth={2.5} fill="url(#revGrad)" dot={{ fill: "#34d399", r: 3, strokeWidth: 0 }} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>
      ) : (tab === "week" || tab === "month") ? (
        <GlassPanel
          title={`So sánh ${tab === "week" ? "tuần này vs tuần trước" : "tháng này vs tháng trước"}`}
          badge="so sánh"
          badgeVariant="info"
        >
          <RevenueComparisonChart period={tab} />
        </GlassPanel>
      ) : null}

      {tab === "custom" && dayPoints.length > 0 && (
        <GlassPanel title="Bảng chi tiết theo ngày" badge={`${dayPoints.length} dòng`} badgeVariant="info" style={{ marginTop: 12 }}>
          <DataTable
            columns={[
              { key: "day", header: "Ngày", render: (r) => r.day.slice(5) },
              { key: "orders", header: "Số đơn", render: (r) => formatNumber(r.orders) },
              { key: "sales", header: "Doanh thu", render: (r) => formatVND(r.sales) },
              {
                key: "aov",
                header: "AOV",
                render: (r) => formatVND(r.orders > 0 ? Math.round(r.sales / r.orders) : 0),
              },
            ]}
            rows={dayPoints}
            rowKey={(r) => r.day}
            emptyMessage="Không có dữ liệu"
          />
        </GlassPanel>
      )}

      {/* Nhóm hàng */}
      <GlassPanel
        title="Doanh thu theo nhóm hàng"
        badge={`${byCat.length} nhóm`}
        badgeVariant="info"
      >
        {byCat.length === 0 ? (
          <div className="empty">Chưa có dữ liệu</div>
        ) : (
          <div className="cat-bars">
            {byCat.map((c) => (
              <button
                type="button"
                className="cat-row"
                key={c.category}
                onClick={() => openCatDrill(c)}
                title="Xem SP đã bán của nhóm"
              >
                <div className="cat-row-head">
                  <span className="cat-name">
                    <span className="cat-swatch" style={{ background: catColor(c.color) }} />
                    {c.category}
                  </span>
                  <span className="cat-amount">{formatVND(c.revenue)}</span>
                </div>
                <div className="cat-bar-track">
                  <div
                    className="cat-bar-fill"
                    style={{
                      width: `${Math.max(2, (c.revenue / Math.max(maxCat, 1)) * 100)}%`,
                      background: catColor(c.color),
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        )}
      </GlassPanel>

      {/* Top items + Recent orders */}
      <div className="grid-2">
        <GlassPanel
          title="Sản phẩm bán chạy"
          badge={searchHits ? `${searchHits.length} SP` : `top ${data.topItems.length}`}
          badgeVariant="info"
        >
          <form
            className="search"
            style={{ marginBottom: 10 }}
            onSubmit={(e) => e.preventDefault()}
          >
            <span className="material-symbols-outlined">search</span>
            <input
              type="search"
              placeholder="Tra SP đã bán…"
              value={productQ}
              onChange={(e) => setProductQ(e.target.value)}
              aria-label="Tìm sản phẩm đã bán"
            />
          </form>
          <div className="list">
            {searchLoading ? (
              <div className="empty">Đang tìm…</div>
            ) : searchHits ? (
              searchHits.length === 0 ? (
                <div className="empty">Không tìm thấy SP bán trong kỳ</div>
              ) : (
                searchHits.map((it, idx) => (
                  <div
                    className="list-item"
                    key={`${it.item_name}-${idx}`}
                    onClick={() => openProductDrill(it.item_name)}
                    style={{ cursor: "pointer" }}
                    title="Xem ngày giờ bán"
                  >
                    <div>
                      <div className="name">{it.item_name}</div>
                      <div className="meta">
                        {it.sku} · {formatNumber(it.qty)} sản phẩm
                      </div>
                    </div>
                    <div className="right">
                      <div className="amount">{formatVND(it.revenue)}</div>
                    </div>
                  </div>
                ))
              )
            ) : data.topItems.length === 0 ? (
              <div className="empty">Chưa có dữ liệu</div>
            ) : (
              data.topItems.map((it, idx) => (
                <div
                  className="list-item"
                  key={it.item_id || idx}
                  onClick={() => openProductDrill(it.item_name || "—")}
                  style={{ cursor: "pointer" }}
                  title="Xem ngày giờ bán"
                >
                  <div>
                    <div className="name">{it.item_name || "—"}</div>
                    <div className="meta">
                      {it.sku || "—"} · {formatNumber(it.qty)} sản phẩm
                    </div>
                  </div>
                  <div className="right">
                    <div className="amount">{formatVND(it.revenue)}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </GlassPanel>

        <GlassPanel
          title="Đơn gần nhất"
          badge={`${data.recentOrders.length} đơn`}
          badgeVariant="ok"
        >
          <div className="list">
            {data.recentOrders.length === 0 ? (
              <div className="empty">Chưa có đơn nào</div>
            ) : (
              data.recentOrders.map((o) => (
                <Link
                  to={`/orders/${o.receipt_number}`}
                  className="list-item"
                  key={o.receipt_number}
                >
                  <div>
                    <div className="name">#{o.receipt_number}</div>
                    <div className="meta">
                      {formatTime(o.receipt_date)} · {paymentLabel(o.payment_type)}
                    </div>
                  </div>
                  <div className="right">
                    <div className="amount">{formatVND(o.total_money)}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </GlassPanel>
      </div>

      <Modal
        open={catDrill.cat != null}
        onClose={closeCatDrill}
        title={catDrill.cat ? `SP nhóm: ${catDrill.cat.category}` : ""}
        width={640}
      >
        {catDrill.loading ? (
          <div className="empty">Đang tải…</div>
        ) : catDrill.products.length === 0 ? (
          <div className="empty">Chưa có SP bán trong kỳ</div>
        ) : (
          <>
            {catDrill.products.length > 1 && (
              <div className="cat-sort">
                <span className="cat-sort-label">Sắp xếp:</span>
                <div className="tabs">
                  <button
                    type="button"
                    className={`tab-item ${catSort.key === "revenue" ? "active" : ""}`}
                    aria-pressed={catSort.key === "revenue"}
                    title="Theo tiền bán được — bấm lần nữa để đổi chiều"
                    onClick={() => pickCatSort("revenue")}
                  >
                    Theo tiền
                    {catSort.key === "revenue" && (
                      <span className="material-symbols-outlined cat-sort-icon">
                        {catSort.dir === "desc" ? "arrow_downward" : "arrow_upward"}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`tab-item ${catSort.key === "qty" ? "active" : ""}`}
                    aria-pressed={catSort.key === "qty"}
                    title="Theo số lượng bán được — bấm lần nữa để đổi chiều"
                    onClick={() => pickCatSort("qty")}
                  >
                    Theo số lượng
                    {catSort.key === "qty" && (
                      <span className="material-symbols-outlined cat-sort-icon">
                        {catSort.dir === "desc" ? "arrow_downward" : "arrow_upward"}
                      </span>
                    )}
                  </button>
                </div>
                <span className="cat-sort-dir" aria-live="polite">
                  {catSort.key ? (catSort.dir === "desc" ? "Giảm dần" : "Tăng dần") : ""}
                </span>
              </div>
            )}
            <div className="list">
              {catRows.map((p, i) => (
                <div className="list-item" key={`${p.item_id || p.item_name}-${p.sku}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="title">{p.item_name}</div>
                    <div className="sub">{p.sku || "—"} · sl {formatNumber(p.qty)}{typeof p.stock === "number" && Number.isFinite(p.stock) ? ` · tồn ${formatNumber(p.stock)}` : ""}</div>
                  </div>
                  <div className="right" style={{ whiteSpace: "nowrap" }}>{formatVND(p.revenue)}</div>
                </div>
              ))}
              <div className="list-item" style={{ display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                <div>Tổng {catDrill.products.length} SP</div>
                <div className="right">{formatVND(catDrill.products.reduce((s, p) => s + (p.revenue || 0), 0))}</div>
              </div>
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={productDrill.name != null}
        onClose={closeProductDrill}
        title={productDrill.name ? `Lịch sử bán: ${productDrill.name}` : ""}
        width={640}
      >
        {productDrill.loading ? (
          <div className="empty">Đang tải…</div>
        ) : productDrill.rows.length === 0 ? (
          <div className="empty">Không có dòng bán trong kỳ</div>
        ) : (
          <div className="list">
            {productDrill.rows.map((r, idx) => {
              const dateStr = r.sale_date
                ? r.sale_date.includes("-")
                  ? r.sale_date.split("-").reverse().join("/")
                  : r.sale_date
                : "—";
              const timeRaw =
                r.sold_at ||
                (r.source === "receipt" ? r.imported_at : null) ||
                r.sale_time ||
                null;
              let timeStr = "—";
              if (timeRaw) {
                const d = new Date(timeRaw);
                if (!Number.isNaN(d.getTime())) {
                  timeStr = d.toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
                }
              }
              return (
                <div className="list-item" key={r.id ?? idx} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="title">{dateStr} · {timeStr}</div>
                    <div className="sub">
                      sl {formatNumber(r.qty)}
                      {r.receipt_number ? (
                        <>
                          {" · "}
                          <Link to={`/orders/${r.receipt_number}`} onClick={(e) => e.stopPropagation()}>
                            #{r.receipt_number}
                          </Link>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <div className="right" style={{ whiteSpace: "nowrap" }}>{formatVND(r.revenue)}</div>
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
}

function KpiCardPro({
  label,
  rawValue,
  formatter,
  delta,
  sparkline,
  sub,
  comparison,
}: {
  label: string;
  rawValue: number;
  formatter: (v: number) => string;
  delta?: number | null;
  sparkline?: number[];
  sub?: string;
  comparison?: { pct: number; prev: number } | null;
}) {
  const animated = useCountUp(rawValue);
  const max = sparkline && sparkline.length > 0 ? Math.max(...sparkline, 1) : 1;
  const dir = delta == null ? null : delta >= 0 ? "up" : "down";
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{formatter(animated)}</div>
      <div className="kpi-note">
        {dir && (
          <span className={`kpi-delta ${dir}`}>
            <span className="material-symbols-outlined">
              {dir === "up" ? "trending_up" : "trending_down"}
            </span>
            {Math.abs(delta!).toFixed(1)}%
          </span>
        )}
        {comparison && (
          <span style={{ marginLeft: dir ? 6 : 0, fontSize: 11, color: "var(--text-tertiary)" }}>
            so với kỳ trước: {formatVND(comparison.prev)}
          </span>
        )}
        {sub && <span style={{ marginLeft: dir ? 6 : 0 }}>{sub}</span>}
        {!dir && !sub && !comparison && <span>so với hôm qua</span>}
      </div>
      {sparkline && sparkline.length > 1 && (
        <div className="kpi-sparkline">
          {sparkline.map((v, i) => (
            <div
              className="bar"
              key={i}
              style={{ height: `${Math.max(8, (v / max) * 100)}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function fillHourly(rows: { hour: number; sales: number; orders: number }[]) {
  const map = new Map<number, { hour: number; sales: number; orders: number }>();
  for (let h = 0; h < 24; h++) map.set(h, { hour: h, sales: 0, orders: 0 });
  for (const r of rows) map.set(r.hour, r);
  return Array.from(map.values());
}

type HourlyMode = "revenue" | "orders";

function HourlyChart({ hourly }: { hourly: { hour: number; sales: number; orders: number }[] }) {
  const [mode, setMode] = useState<HourlyMode>("revenue");
  const filled = fillHourly(hourly);
  const dataKey = mode === "revenue" ? "sales" : "orders";

  return (
    <GlassPanel
      title="Phân bổ thời gian bán"
      badge="theo giờ"
      badgeVariant="info"
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
        <button
          className={`tab-item ${mode === "revenue" ? "active" : ""}`}
          style={{ fontSize: 11, padding: "3px 10px" }}
          onClick={() => setMode("revenue")}
        >
          Theo doanh thu
        </button>
        <button
          className={`tab-item ${mode === "orders" ? "active" : ""}`}
          style={{ fontSize: 11, padding: "3px 10px" }}
          onClick={() => setMode("orders")}
        >
          Theo đơn
        </button>
      </div>
      <div style={{ height: 160 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={filled} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
            <XAxis dataKey="hour" stroke="#666" fontSize={10} tickFormatter={(h) => `${h}h`} />
            <YAxis
              stroke="#666"
              fontSize={10}
              tickFormatter={(v: number) =>
                mode === "revenue" ? `${(v / 1000).toFixed(0)}k` : String(v)
              }
              width={35}
            />
            <Tooltip
              contentStyle={{
                background: 'rgba(15, 15, 15, 0.95)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                borderRadius: 8,
                padding: '8px 12px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
              }}
              labelStyle={{ color: '#f5f5f7', fontWeight: 600, marginBottom: 4 }}
              itemStyle={{ color: '#34d399' }}
              labelFormatter={(h: number) => `${h}:00 – ${h + 1}:00`}
              formatter={(_v: number, name: string, props: any) => {
                const d = props.payload;
                return [
                  `${formatVND(d.sales)} · ${formatNumber(d.orders)} đơn`,
                  mode === "revenue" ? "Doanh thu" : "Đơn hàng",
                ];
              }}
            />
            <Bar dataKey={dataKey} name={dataKey} radius={[4, 4, 0, 0]}>
              {filled.map((d, i) => (
                <Cell
                  key={i}
                  fill={
                    d[dataKey] === 0
                      ? "rgba(255,255,255,0.04)"
                      : d.hour >= 11 && d.hour <= 14
                      ? "#fbbf24"
                      : d.hour >= 18 && d.hour <= 21
                      ? "#a78bfa"
                      : "#60a5fa"
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </GlassPanel>
  );
}

function peakHourLabel(rows: { hour: number; sales: number; orders: number }[]) {
  if (!rows || rows.length === 0) return "—";
  const peak = rows.reduce((a, b) => (a.sales > b.sales ? a : b), rows[0]);
  return `${peak.hour}:00 – ${peak.hour + 1}:00`;
}

function DashboardSkeleton() {
  return (
    <>
      <div className="kpi-grid">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="kpi-card skeleton" style={{ height: 120 }} />
        ))}
      </div>
      <div className="skeleton" style={{ height: 220, marginBottom: 16, borderRadius: 14 }} />
      <div className="grid-2">
        <div className="skeleton" style={{ height: 220, borderRadius: 14 }} />
        <div className="skeleton" style={{ height: 220, borderRadius: 14 }} />
      </div>
    </>
  );
}